
import React, { memo, useEffect, useRef, useState } from 'react';
import _, { isEmpty } from 'lodash';
import { FieldArray, FastField } from 'formik';
import { FORM_COMPONENT, DEFAULT_TYPE, BUTTON, INPUT } from './../types/inputTypes';
import { toAbsoluteUrl } from 'src/helpers';
import SVG from "react-inlinesvg"
import { useFormikContext } from "formik";
import useHideAndShowField from './hooks/useHideAndShowField';

const LoadingSpinner = memo(() => (
  <div className="d-flex justify-content-center align-items-center p-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Chargement...</span>
    </div>
  </div>
));
const EnhancedFormRepeaterTable = (props) => {
  const addRef = useRef()
  const regex = new RegExp('[0-9]')
  const formik = useFormikContext();
  const isLoading = formik.status?.isLoading || false;

  const {
    label,
    min = 0,
    field,
    fields,
    form,
    showDeleteButton = true,
    showAddButton = true,
    max = 50,
    hideOnET,
    hide,
    title,
    disabled = false,
    notEmpty = false,
    showActionColumn = false,
    showActionColumnLabel = true,
    hideWhenEmpty = false,
    actionColumnFormatter,
    setAllTouched = false,
    showRowCheckbox = false,
    // ⚠️ Use these props directly as the source of truth
    checkedRows,
    setCheckedRows,
    checkboxDisabled,
    key
  } = props

  // ⚠️ NO INTERNAL STATE/USESTATE FOR checkedRows HERE

  const conditionalShowAndHideClassName = useHideAndShowField({
    formik,
    fieldName: field.name,
    ...props,
    hideOnET
  });

  const arrayFieldValues = React.useMemo(() => {
    const value = _.get(form.values, field.name);
    return Array.from(Array.isArray(value) ? value : []);
  }, [form.values, field.name])

  const initialSnapshot = React.useMemo(() => {
    const snapshot = {}
    fields.forEach(field => {
      _.set(snapshot, field.name.substring(field.name.lastIndexOf('[]') + 1), _.get(field, 'initialValue', ''))
    })
    return snapshot
  }, [fields])

  // 1. FIX: Robust cleanup effect with correct dependencies
  useEffect(() => {
    if (!setCheckedRows || !checkedRows) return;

    const currentLength = arrayFieldValues.length;

    const newCheckedRows = new Set();
    let needsUpdate = false;

    checkedRows.forEach(index => {
      if (index >= 0 && index < currentLength) {
        // Only keep enabled checkboxes
        if (!checkboxDisabled || !checkboxDisabled(arrayFieldValues[index], index)) {
          newCheckedRows.add(index);
        } else {
          needsUpdate = true;
        }
      } else {
        needsUpdate = true;
      }
    });

    if (needsUpdate || newCheckedRows.size !== checkedRows.size) {
      setCheckedRows(newCheckedRows);
    }

    // ⚠️ CRITICAL DEPENDENCIES
  }, [arrayFieldValues.length, arrayFieldValues, checkedRows, setCheckedRows, checkboxDisabled]);


  useEffect(() => {
    if (setAllTouched && arrayFieldValues.length > 0) {
      // ... (rest of the setAllTouched logic remains unchanged)
      arrayFieldValues.forEach((_, index) => {
        fields.forEach(fieldItem => {
          const fieldPath = `${field.name}.${index}.${fieldItem.name.substring(fieldItem.name.lastIndexOf('[]') + 1)}`;
          form.setFieldTouched(fieldPath, true, false);
        });
      });
      form.validateForm();
    }
  }, [setAllTouched, arrayFieldValues, fields, field.name, form]);

  const renderField = ({ name, arrayName, component, conditionalComponent, conditionalDisplayValue, i, rowIndex, ...props }) => {
    // Handle conditional component based on row data
    if (conditionalComponent && typeof conditionalComponent === 'function') {
      const rowData = arrayFieldValues[rowIndex];
      component = conditionalComponent(rowData, rowIndex);

      // Remove SELECT-specific props when component is INPUT
      if (component === INPUT) {
        const { chainedOptions, options, multiple, relyOn, chainedField, getFirstValue, ...inputProps } = props;
        props = inputProps;
        // Handle conditional display value for INPUT
        if (conditionalDisplayValue && typeof conditionalDisplayValue === 'function') {
          const currentValue = _.get(form.values, arrayName + name);
          const displayValue = conditionalDisplayValue(rowData, currentValue, rowIndex);
          if (displayValue !== undefined) {
            props.displayValue = displayValue;
          }
        }
      }
    }

    if (component !== BUTTON) {
      props.label = ""
    }

    // For BUTTON components, wrap onClick to pass row data
    if (component === BUTTON && props.onClick) {
      const originalOnClick = props.onClick;
      props.onClick = () => {
        const rowData = arrayFieldValues[rowIndex];
        originalOnClick(rowData, rowIndex);
      };
    }

    // Handle dynamic disabled function
    if (typeof props.disabled === 'function') {
      const fieldPath = arrayName + name;
      props.disabled = props.disabled(form.values, fieldPath);
    }

    return (
      <td style={{ minWidth: "200px", borderBottom: '2px solid #e0e0e0', padding: '0' }} key={i}>
        <FastField
          name={arrayName + name}
          disabled={props.disabled !== undefined ? props.disabled : disabled}
          component={FORM_COMPONENT[component] || DEFAULT_TYPE}
          {...props}
          arrayName={arrayName}
          inputGroupClassName=""
        />
      </td>
    )
  }

  const renderFields = (arrayName, fields, rowIndex) => (<>
    {fields.filter(field => !field.hide).map((input, i) => {

      const { validation, ...field } = input
      const { hideOn } = field;
      let field2
      if (!_.isEmpty(hideOn)) {
        field2 = { ...field, hideOn: `${arrayName}${hideOn}` }

      }
      else {
        field2 = { ...field }
      }

      if (field2.show && typeof field2.show === 'function') {
        const originalShow = field2.show;
        field2.show = (formValues) => {
          return originalShow(formValues, arrayName + field2.name);
        };
      }

      return (
        renderField({
          ...field2,
          arrayName,
          i,
          rowIndex
        })
      )
    })}
  </>)

  // 2. FIX: Handler uses functional update for parent state
  const handleRowCheck = (index) => {
    if (!setCheckedRows) return;
    // Prevent checking if checkbox is disabled
    if (checkboxDisabled && checkboxDisabled(arrayFieldValues[index], index)) return;

    setCheckedRows(prevCheckedRows => {
      const newCheckedRows = new Set(prevCheckedRows);
      if (newCheckedRows.has(index)) {
        newCheckedRows.delete(index);
      } else {
        newCheckedRows.add(index);
      }
      return newCheckedRows;
    });
  };

  // 3. FIX: Handler for all check
  const handleCheckAll = (event) => {
    if (!setCheckedRows) return;
    const isChecked = event.target.checked;
    if (isChecked) {
      // Only include enabled checkboxes
      const enabledIndexes = arrayFieldValues
        .map((row, index) => ({ row, index }))
        .filter(({ row, index }) => !checkboxDisabled || !checkboxDisabled(row, index))
        .map(({ index }) => index);
      setCheckedRows(new Set(enabledIndexes));
    } else {
      setCheckedRows(new Set());
    }
  };

  // Ensure checkedRows is a Set before using .size or .has
  const validCheckedRows = checkedRows instanceof Set ? checkedRows : new Set();

  // Calculate enabled checkboxes count
  const enabledCheckboxesCount = arrayFieldValues.filter((row, index) =>
    !checkboxDisabled || !checkboxDisabled(row, index)
  ).length;

  const isAllChecked = enabledCheckboxesCount > 0 && validCheckedRows.size === enabledCheckboxesCount;


  if (hide)
    return <></>

  if (isEmpty(field.value) && hideWhenEmpty) {
    return <></>
  }

  return (<>
    {/* ... (render logic is unchanged) */}
    <h3 style={{
      paddingRight: "12.5px",
      paddingLeft: "12.5px",
      marginTop: "12.5px"
    }} className={"form-repeater-table-title" + conditionalShowAndHideClassName}>{label}</h3>
    <div key={key || field.name} style={{ overflowX: "overlay", maxHeight: "600px", overflowY: "auto" }} className={"pb-3 react-bootstrap-table responsive-table-nHeight col-lg-12" + conditionalShowAndHideClassName} >
      {title}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <table className="table table-head-custom table-vertical-center mb-0">
          <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#dc3545', borderWidth: '0px 1px 1px 1px', borderStyle: 'solid', borderColor: '#dc3545' }}>
            <tr lg='1'>
              {/* 4. Checkbox Header Column */}
              {showRowCheckbox && (
                <th style={{ width: '5%', textAlign: 'center', color: '#ffffff' }}>
                  <input
                    type="checkbox"
                    checked={isAllChecked}
                    onChange={handleCheckAll}
                    disabled={arrayFieldValues.length === 0}
                    style={{
                      transform: 'scale(1.5)',
                      accentColor: '#dc3545', // Bootstrap danger red
                      borderRadius: '6px', // more rounded corners
                      border: '2px solid #fff)', // soft white border
                      backgroundColor: '#f7f7f7ff',
                      cursor: 'pointer',
                    }}
                  /* style={{ 
                      transform: 'scale(1.5)',
                      accentColor: '#dc3545'
                  }} */
                  />
                </th>
              )}

              {fields.filter(field => !field.hide).map((field, index) => (
                <th key={index} style={{
                  ...(field.component === BUTTON ? { paddingLeft: '0', paddingRight: '0' } : {}),
                  color: '#ffffff',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '0.85rem',
                  lineHeight: '1.2rem',
                  textAlign: 'center',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem'
                }}>{field.label}</th>
              ))}
              {showDeleteButton && <th style={{ width: '5%' }}></th>}
              {showActionColumn && actionColumnFormatter.map((item, i) => (
                <th key={i} style={{
                  width: '5%',
                  color: '#ffffff',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '0.85rem',
                  lineHeight: '1.2rem',
                  textAlign: 'center',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem'
                }}>{item.label}</th>
              ))}
            </tr>
          </thead>

          <FieldArray
            name={field.name}
            render={(arrayHelpers) => (
              <tbody>
                {_.isArray(arrayFieldValues) && arrayFieldValues.length > 0 ? (
                  arrayFieldValues.map((__, index) => (
                    <tr key={index} style={{ borderBottom: '2px solid #e0e0e0' }}>
                      {/* 5. Checkbox Row Cell */}
                      {showRowCheckbox && (
                        <td style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'center', width: '5%' }} className='td-padding-bottom'>
                          <input
                            type="checkbox"
                            // Use the prop directly for checking state
                            checked={validCheckedRows.has(index)}
                            onChange={() => handleRowCheck(index)}
                            disabled={checkboxDisabled ? checkboxDisabled(arrayFieldValues[index], index) : false}
                            style={{
                              transform: 'scale(1.5)',
                              accentColor: '#dc3545', // Bootstrap danger red
                              borderRadius: '6px', // more rounded corners
                              border: '2px solid rgba(255,255,255,0.8)', // soft white border
                              backgroundColor: '#fff',
                              cursor: (checkboxDisabled && checkboxDisabled(arrayFieldValues[index], index)) ? 'not-allowed' : 'pointer',
                            }}
                          />
                        </td>
                      )}

                      {renderFields(`${field.name}.${index}.`, fields, index)}

                      {showDeleteButton && index > 0 && (((notEmpty && arrayFieldValues.length > 1) || !notEmpty) ? < td style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'center', width: '5%' }} className='td-padding-bottom'>
                        <SVG type="button" src={toAbsoluteUrl("/media/svg/x.svg")} onClick={() => arrayHelpers.remove(index)} />
                      </td> : <></>)}
                      {showActionColumn && actionColumnFormatter && (
                        actionColumnFormatter.map((formatterFn, i) => (
                          <td
                            key={i} // Key is mandatory when mapping
                            style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'center', width: '5%' }}
                            className='td-padding-bottom'
                          >
                            {/* 💥 CORRECT CALL SYNTAX: formatterFn(...) */}
                            {formatterFn.formatter(null, __, index, {
                              onActionClick: props.onActionClick,
                              form: form,
                              fieldName: field.name,
                              campaign: props.campaign
                            })}
                          </td>
                        ))
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={fields.filter(field => !field.hide).length + (showRowCheckbox ? 1 : 0) + 1} style={{ textAlign: 'center', border: 'none' }}>
                      {/* No data available */}
                    </td>
                  </tr>
                )}
                {showAddButton && (
                  <tr>
                    <td colSpan={fields.filter(field => !field.hide).length + (showRowCheckbox ? 1 : 0) + 1} style={{ border: 'none', textAlign: 'center' }}>
                      <SVG type="button" src={toAbsoluteUrl("/media/svg/plus.svg")} onClick={() => arrayHelpers.push({ ...initialSnapshot })} />
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          />
        </table>
      )}


    </div >
  </>
  );
};

export default EnhancedFormRepeaterTable;